import type { DevString, Falsey, Handler } from "@phosphor/utils";
import type { Atom, PrimitiveAtom } from "jotai";
import type { DefaultKeyCombo } from "#scripts/keyboard-demo/utils/key-combo.ts";
import { type UID, World } from "#scripts/vmc/WorldState.ts";

// #region cactions-component
export type AnyAction = {
  label: string;
  defaultKeybinding: DefaultKeyCombo;
  description?: string;
  icon?: any;
  self?: boolean;
  hideFromLastPressed?: boolean;
};
type AnyBindables = Record<string, AnyAction>;

export type ActionEvent = { target: UID };

export namespace CActions {
  export type Bindings<T extends AnyBindables> = {
    [P in keyof T]: Handler<ActionEvent> | Falsey;
  };
}

export type ActionBindings<T extends AnyBindables = AnyBindables> = {
  bindingSource: DevString;
  registryKey: ActionRegistryKey<T>;
  bindingsAtom: Atom<CActions.Bindings<T>>;
};

export class ActionRegistryKey<T extends AnyBindables = AnyBindables> {
  constructor(
    public readonly key: string,
    public readonly meta: { source: DevString; sectionName: string },
    public readonly bindables: T,
  ) {}
}

export class CActions extends World.Component("actions")<CActions, ActionBindings[]>() {
  static bind<T extends AnyBindables>(key: ActionBindings<T>) {
    return CActions.of([key as ActionBindings]);
  }

  static merge(...bindings: Array<ActionBindings | ActionBindings[]>) {
    const out: ActionBindings[] = [];
    for (const b of bindings) {
      if (Array.isArray(b)) {
        out.push(...b);
      } else {
        out.push(b);
      }
    }
    return CActions.of(out);
  }

  static defineActions<T extends AnyBindables>(
    key: string,
    meta: { source: DevString; sectionName: string },
    actions: T,
  ): ActionRegistryKey<T> {
    return new ActionRegistryKey(key, meta, actions);
  }
}
// #endregion cactions-component

export class UKeydownRootHandler extends World.Unique("_KeydownRootHandler")<
  UKeydownRootHandler,
  {
    handler: Handler<KeyboardEvent>;
    rootUIDAtom: PrimitiveAtom<UID | null>;
  }
>() {}
